import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Shield, Copy, Check, ExternalLink, Package, DollarSign, Users, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import SEO from "../components/SEO";
import api from "../lib/api";

const TOKEN_KEY = "rc_admin_token";

export default function AdminOrders() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [statusFilter, setStatusFilter] = useState("paid");

    const authHeaders = useCallback(() => ({ headers: { "X-Admin-Token": token } }), [token]);

    const fetchAll = useCallback(async (tok) => {
        const useToken = tok || token;
        if (!useToken) return;
        setLoading(true);
        try {
            const [statsRes, ordersRes] = await Promise.all([
                api.get("/admin/stats", { headers: { "X-Admin-Token": useToken } }),
                api.get(`/admin/orders?status=${statusFilter}`, { headers: { "X-Admin-Token": useToken } }),
            ]);
            setStats(statsRes.data);
            setOrders(ordersRes.data.orders || []);
            setAuthed(true);
            localStorage.setItem(TOKEN_KEY, useToken);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401) {
                toast.error("Invalid admin token");
                setAuthed(false);
                localStorage.removeItem(TOKEN_KEY);
            } else {
                toast.error("Failed to load admin data");
            }
        } finally {
            setLoading(false);
        }
    }, [token, statusFilter]);

    useEffect(() => { if (authed) fetchAll(); }, [authed, statusFilter, fetchAll]);

    const handleLogin = (e) => {
        e.preventDefault();
        if (!token.trim()) return toast.error("Enter an admin token");
        fetchAll(token.trim());
    };

    const toggleFulfilled = async (sessionId, current) => {
        try {
            await api.patch(`/admin/orders/${sessionId}`, { fulfilled: !current }, authHeaders());
            toast.success(!current ? "Marked as fulfilled" : "Marked as unfulfilled");
            fetchAll();
        } catch {
            toast.error("Update failed");
        }
    };

    const updateNotes = async (sessionId, notes) => {
        try {
            await api.patch(`/admin/orders/${sessionId}`, { fulfillment_notes: notes }, authHeaders());
            toast.success("Notes saved");
        } catch {
            toast.error("Save failed");
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
        setStats(null);
        setOrders([]);
    };

    if (!authed) {
        return (
            <div className="max-w-md mx-auto px-6 py-24">
                <SEO title="Admin · Real-Check" path="/admin/orders" noindex />
                <Card className="p-8 border-white/10 bg-card/70">
                    <div className="flex items-center gap-2.5 mb-6">
                        <Shield className="h-5 w-5 text-primary" />
                        <h1 className="font-display font-bold text-2xl text-accent">Admin Access</h1>
                    </div>
                    <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
                        <div>
                            <label className="block text-sm text-muted-foreground mb-2">Admin token</label>
                            <Input
                                type="password"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                placeholder="Enter ADMIN_CRON_TOKEN"
                                data-testid="admin-token-input"
                                autoFocus
                            />
                        </div>
                        <Button type="submit" className="w-full rounded-full" data-testid="admin-login-submit">
                            Continue
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                            Token is stored locally in your browser. Never share it.
                        </p>
                    </form>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="admin-orders-page">
            <SEO title="Admin Orders · Real-Check" path="/admin/orders" noindex />

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">
                <div>
                    <h1 className="font-display font-bold text-3xl sm:text-4xl text-accent">Admin · Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">Manual fulfillment queue for Printful.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => fetchAll()} disabled={loading}>
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn">Sign out</Button>
                </div>
            </div>

            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                    <StatCard icon={DollarSign} label="Total revenue" value={`$${stats.revenue.total.toFixed(2)}`} sub={`Merch: $${stats.revenue.merch.toFixed(2)}`} />
                    <StatCard icon={Package}    label="Paid orders"   value={stats.orders.paid} sub={`Pending: ${stats.orders.pending_fulfillment}`} tone="amber" />
                    <StatCard icon={Users}      label="Users"         value={stats.users.total} sub={`Pro: ${stats.users.pro}`} />
                    <StatCard icon={Zap}        label="Fact-checks"   value={stats.fact_checks.total} sub={`24h: ${stats.fact_checks.last_24h} · 7d: ${stats.fact_checks.last_7d}`} />
                </div>
            )}

            <div className="flex items-center gap-2 mb-4 text-sm">
                {["paid", "initiated", "all"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 rounded-full border transition-colors ${
                            statusFilter === s
                                ? "border-primary/60 bg-primary/15 text-primary"
                                : "border-white/10 text-muted-foreground hover:text-accent"
                        }`}
                        data-testid={`filter-${s}`}
                    >
                        {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="py-16 text-center text-muted-foreground"><Loader2 className="h-6 w-6 mx-auto animate-spin" /></div>
            ) : orders.length === 0 ? (
                <Card className="p-12 text-center border-white/10 bg-card/50">
                    <Package className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">No orders yet with status &ldquo;{statusFilter}&rdquo;.</p>
                </Card>
            ) : (
                <div className="space-y-3" data-testid="orders-list">
                    {orders.map((o) => (
                        <OrderRow
                            key={o.session_id}
                            order={o}
                            onToggle={() => toggleFulfilled(o.session_id, o.fulfilled)}
                            onSaveNotes={(notes) => updateNotes(o.session_id, notes)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function StatCard({ icon: Icon, label, value, sub, tone = "primary" }) {
    const toneClass = tone === "amber" ? "text-amber-300" : "text-primary";
    return (
        <Card className="p-4 border-white/10 bg-card/60">
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                <Icon className={`h-3.5 w-3.5 ${toneClass}`} />
                {label}
            </div>
            <div className="font-display font-bold text-2xl text-accent mt-1.5">{value}</div>
            {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </Card>
    );
}

function OrderRow({ order, onToggle, onSaveNotes }) {
    const [notes, setNotes] = useState(order.fulfillment_notes || "");
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(!order.fulfilled);

    const shipBlock = `${order.customer_name || "—"}\n${order.email || ""}\n${order.ship_country || ""}\n\nItem: ${order.product_name} (${order.color}, ${order.size}) × ${order.quantity}\nStripe Session: ${order.session_id}`;

    const copy = () => {
        navigator.clipboard.writeText(shipBlock);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
        toast.success("Copied ship details");
    };

    return (
        <Card className={`p-4 border-white/10 transition-opacity ${order.fulfilled ? "opacity-60 bg-card/40" : "bg-card/70"}`} data-testid={`order-${order.session_id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-accent">{order.product_name}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-muted-foreground border border-white/10">{order.color} · {order.size}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">× {order.quantity}</span>
                        <span className="text-xs text-muted-foreground">${order.amount.toFixed(2)}</span>
                        {order.fulfilled && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">Fulfilled</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${order.payment_status === "paid" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-500/30"}`}>
                            {order.payment_status}
                        </span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1 truncate">
                        {order.customer_name || "—"} · {order.email} {order.ship_country ? `· ${order.ship_country}` : ""}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 font-mono truncate">{order.session_id}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={copy} data-testid={`copy-order-${order.session_id}`}>
                        {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
                        Copy
                    </Button>
                    <Button
                        size="sm"
                        variant={order.fulfilled ? "outline" : "default"}
                        onClick={onToggle}
                        data-testid={`toggle-fulfilled-${order.session_id}`}
                    >
                        {order.fulfilled ? "Unmark" : "Mark fulfilled"}
                    </Button>
                    <button onClick={() => setExpanded(!expanded)} className="text-xs text-muted-foreground hover:text-accent transition-colors">
                        {expanded ? "Hide" : "Notes"}
                    </button>
                </div>
            </div>
            {expanded && (
                <div className="mt-3 pt-3 border-t border-white/5">
                    <label className="text-xs text-muted-foreground uppercase tracking-wider">Fulfillment notes (Printful order #, tracking, etc.)</label>
                    <div className="flex gap-2 mt-1.5">
                        <Textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Printful order #12345 · shipped 2026-07-30 · USPS 9400..."
                            className="text-sm"
                            data-testid={`notes-${order.session_id}`}
                        />
                        <Button size="sm" variant="outline" onClick={() => onSaveNotes(notes)} className="self-start">
                            Save
                        </Button>
                    </div>
                    <a
                        href="https://www.printful.com/dashboard/default/orders/new"
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-2"
                    >
                        Open Printful new order <ExternalLink className="h-3 w-3" />
                    </a>
                </div>
            )}
        </Card>
    );
}
